/**
 * BaaS Stables transactions via MobileService `/v1/ib/console/*` → `v1/stables/accounts/{id}/transactions`.
 */

import { baasProxy } from "./baasConsoleClient";
import { fetchBaasAccounts } from "./baasAccountsListApi";

export const BAAS_STABLES_OPERATIONS = ["buy", "sell", "swap", "transfer", "mint", "burn"];
export const BAAS_STABLES_STATUSES = ["processed", "pending", "failed"];
export const BAAS_STABLES_ASSETS = ["USDC", "USDT", "BRZ", "EURC"];

export const getBaasStableClientsFromTransactions = (transactions = []) => {
  const byId = new Map();
  for (const tx of transactions) {
    const id = tx?.clientId;
    if (!id || byId.has(id)) continue;
    byId.set(id, { id, name: tx.clientName || id });
  }
  return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

/** @deprecated Prefer getBaasStableClientsFromTransactions(liveRows) */
export const getBaasStableClients = () => [];

const METADATA_FIELD_ORDER = [
  "external_id",
  "internal_id",
  "correlation_id",
  "provider_ref",
  "tx_hash",
  "block_number",
  "network",
  "wallet_from",
  "wallet_to",
  "gas_used",
  "confirmations",
  "initiated_by",
];

const buildMetadata = (overrides = {}) => ({
  external_id: "",
  internal_id: "",
  correlation_id: "",
  provider_ref: "",
  tx_hash: "",
  block_number: "",
  network: "",
  wallet_from: "",
  wallet_to: "",
  gas_used: "",
  confirmations: "",
  initiated_by: "",
  ...overrides,
});

export const getBaasStableMetadataLabelKey = (fieldKey) =>
  `baas.stables.metadata.fields.${fieldKey}`;

export const getBaasStableMetadataEntries = (transaction) => {
  if (!transaction?.metadata) return [];

  const seen = new Set();
  const entries = [];

  const pushEntry = (key) => {
    if (seen.has(key)) return;
    const value = transaction.metadata[key];
    if (value == null || value === "") return;
    seen.add(key);
    entries.push({ key, value });
  };

  METADATA_FIELD_ORDER.forEach(pushEntry);
  Object.keys(transaction.metadata).forEach(pushEntry);

  return entries;
};

const mapStableType = (type) => {
  const value = String(type || "").toLowerCase();
  if (value.includes("purchase") || value === "buy") return "buy";
  if (value.includes("sale") || value === "sell") return "sell";
  if (value.includes("conversion") || value === "swap") return "swap";
  if (value.includes("withdraw") || value.includes("transfer")) return "transfer";
  if (value.includes("mint")) return "mint";
  if (value.includes("burn")) return "burn";
  return "transfer";
};

const mapStableStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("fail") || value.includes("cancel")) return "failed";
  if (value.includes("pend") || value.includes("process") || value.includes("allocat")) {
    return "pending";
  }
  return "processed";
};

/**
 * Resolve displayable stable quantity (never fiat BRL paid on purchases).
 * Mirrors MobileService StablesHistoryMapper:
 * - buy / purchase → destination stable qty
 * - sell / sale → source stable qty
 * - other → prefer stable leg over fiat
 */
const resolveStableDisplayAmount = (item, operation) => {
  const sourceAmount = Number(
    item?.source?.amount ?? item?.gross_source_amount ?? item?.net_source_amount ?? 0,
  );
  const destAmount = Number(
    item?.destination?.amount
      ?? item?.net_destination_amount
      ?? item?.gross_destination_amount
      ?? 0,
  );
  const sourceKind = String(item?.source?.kind || "").toLowerCase();
  const destKind = String(item?.destination?.kind || "").toLowerCase();
  const sourceIsFiat =
    sourceKind === "fiat" || Boolean(item?.source?.fiat_currency || item?.source?.fiatCurrency);
  const destIsFiat =
    destKind === "fiat" || Boolean(item?.destination?.fiat_currency || item?.destination?.fiatCurrency);

  if (operation === "buy") {
    const stable = destAmount > 0 ? destAmount : Number(item?.stable_amount ?? 0);
    return Number.isFinite(stable) ? stable : 0;
  }
  if (operation === "sell") {
    const stable = sourceAmount > 0 ? sourceAmount : Number(item?.stable_amount ?? 0);
    return Number.isFinite(stable) ? stable : 0;
  }

  if (!sourceIsFiat && sourceAmount > 0) return sourceAmount;
  if (!destIsFiat && destAmount > 0) return destAmount;
  if (destAmount > 0 && sourceIsFiat) return destAmount;
  if (sourceAmount > 0 && destIsFiat) return sourceAmount;

  const fallback = Number(item?.amount ?? item?.stable_amount ?? 0);
  return Number.isFinite(fallback) ? fallback : 0;
};

const resolveStableDisplayAsset = (item, operation) => {
  if (operation === "buy") {
    return String(
      item?.destination?.asset || item?.asset || item?.asset_symbol || "USDC",
    ).toUpperCase();
  }
  if (operation === "sell") {
    return String(
      item?.source?.asset || item?.destination?.asset || item?.asset || item?.asset_symbol || "USDC",
    ).toUpperCase();
  }
  if (operation === "swap") {
    return String(
      item?.source?.asset || item?.destination?.asset || item?.asset || item?.asset_symbol || "USDC",
    ).toUpperCase();
  }
  const destKind = String(item?.destination?.kind || "").toLowerCase();
  const sourceKind = String(item?.source?.kind || "").toLowerCase();
  if (destKind !== "fiat" && item?.destination?.asset) {
    return String(item.destination.asset).toUpperCase();
  }
  if (sourceKind !== "fiat" && item?.source?.asset) {
    return String(item.source.asset).toUpperCase();
  }
  return String(
    item?.destination?.asset || item?.source?.asset || item?.asset || item?.asset_symbol || "USDC",
  ).toUpperCase();
};

const mapStableTransactionToUi = (item, account) => {
  const operation = mapStableType(item?.transaction_type || item?.type || item?.operation);
  const amount = resolveStableDisplayAmount(item, operation);
  const asset = resolveStableDisplayAsset(item, operation);
  const sourceAmount = Number(item?.source?.amount ?? item?.gross_source_amount ?? 0);
  const destAmount = Number(
    item?.destination?.amount ?? item?.net_destination_amount ?? item?.gross_destination_amount ?? 0,
  );
  const explicitRate = Number(item?.rate);
  const amountBrl =
    operation === "buy"
      ? sourceAmount
      : operation === "sell"
        ? destAmount
        : Number.isFinite(explicitRate) && explicitRate > 0
          ? amount * explicitRate
          : 0;
  // Implied cotação when Baas omits/zeroes Rate (still show 4dp from BRL÷stable legs).
  let rate =
    Number.isFinite(explicitRate) && explicitRate > 0 ? explicitRate : null;
  if (rate == null && (operation === "buy" || operation === "sell")) {
    const brl = Number(amountBrl);
    const stable = Number(amount);
    if (
      Number.isFinite(brl) &&
      Number.isFinite(stable) &&
      brl > 0 &&
      stable > 0 &&
      Math.abs(brl - stable) > 0.01
    ) {
      rate = Math.round((brl / stable) * 10000) / 10000;
    }
  }
  const fromAsset =
    operation === "swap"
      ? String(item?.source?.asset || asset || "").toUpperCase()
      : null;
  const toAsset =
    operation === "swap"
      ? String(item?.destination?.asset || "").toUpperCase()
      : null;

  return {
    id: item?.transaction_id || item?.id,
    referenceId: item?.external_id || item?.transaction_id || item?.id || "—",
    operation,
    asset,
    from: fromAsset || undefined,
    to: toAsset || undefined,
    status: mapStableStatus(item?.status),
    clientId: account.entityId || account.id,
    clientName: account.entityName || account.alias || account.id,
    amount,
    amountBrl: Number.isFinite(amountBrl) ? amountBrl : 0,
    amountStable: amount,
    amountFrom: operation === "swap" ? sourceAmount || amount : undefined,
    amountTo: operation === "swap" ? (destAmount > 0 ? destAmount : undefined) : undefined,
    rate,
    network: item?.destination?.network || item?.source?.network || item?.network || item?.chain || "",
    createdAt: item?.created_at || item?.createdAt || null,
    processedAt: item?.updated_at || item?.updatedAt || item?.created_at || item?.createdAt || null,
    instanceId: account.instanceId,
    accountId: account.id,
    walletId: item?.wallet_id || item?.walletId || null,
    balanceId: item?.balance_id || item?.balanceId || null,
    metadata: buildMetadata({
      external_id: item?.external_id || "",
      internal_id: item?.transaction_id || item?.id || "",
      tx_hash: item?.tx_hash || item?.transaction_hash || "",
      network: item?.destination?.network || item?.network || "",
      wallet_from: item?.from_address || "",
      wallet_to: item?.to_address || "",
    }),
    raw: item,
  };
};

export const fetchBaasStablesTransactions = async (instance, options = {}) => {
  if (!instance) return [];

  const accounts =
    options.accounts ?? (await fetchBaasAccounts(instance).catch(() => []));
  if (!accounts.length) return [];

  const batches = await Promise.all(
    accounts.map(async (account) => {
      try {
        const data = await baasProxy({
          instance,
          method: "GET",
          baasPath: `v1/stables/accounts/${encodeURIComponent(account.id)}/transactions`,
        });
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        return items.map((item) => mapStableTransactionToUi(item, account));
      } catch {
        return [];
      }
    }),
  );

  return batches
    .flat()
    .filter((item) => item?.id)
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
};

/** @deprecated Prefer fetchBaasStablesTransactions */
export const getMockBaasStablesTransactions = () => [];

export const getBaasStableTransactionById = (transactions, transactionId) => {
  const rows = Array.isArray(transactions) ? transactions : [];
  return rows.find((item) => item.id === transactionId) ?? null;
};

export const formatBaasStableAmount = (amount, asset = "USDC") => {
  const value = Number(amount) || 0;
  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value)} ${asset}`;
};
